import { supabase } from "@/integrations/supabase/client";

export type LogType =
  | "LOGIN"
  | "LOGOUT"
  | "DEVICE_APPROVAL"
  | "MESSAGE_SEND"
  | "ENCRYPT_ERROR"
  | "DECRYPT_ERROR"
  | "SLOW_OPERATION"
  | "ERROR"
  | "SESSION_ROTATE"
  | "PAYMENT_SEND"
  | "PAYMENT_ERROR"
  | "AUTH_ERROR"
  | "AI_REPLY_ERROR"
  | "AI_MEDIA_ERROR"
  | "AI_TOOL_FALLBACK"
  | "CALL_PUSH_ERROR"
  | "EMAIL_QUEUE_ERROR"
  | "TRANSLATION_ERROR"
  | "DISPATCHER_ERROR"
  | "EDGE_5XX";

export type LogSeverity = "critical" | "error" | "warning" | "info";

const SLOW_THRESHOLD_MS = 2000;

/** Weak fallback hash used when crypto.subtle is unavailable. */
function weakHash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(16).padStart(8, "0");
}

/**
 * SHA-256 hex of `${type}|${message[0..200]}` — matches the server-side
 * trigger `app_logs_to_incident` so client and edge-function logs collapse
 * into a single incident row.
 */
async function fingerprintFor(type: string, message: string): Promise<string> {
  const input = `${type}|${(message || "").slice(0, 200)}`;
  try {
    if (typeof crypto !== "undefined" && crypto.subtle) {
      const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
      return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch { /* fall through */ }
  return weakHash(input);
}

/** Default severity per type. */
function defaultSeverity(type: string): LogSeverity {
  if (type === "ERROR" || type === "ENCRYPT_ERROR" || type === "DECRYPT_ERROR") return "error";
  if (type === "SLOW_OPERATION") return "warning";
  return "info";
}

const DEV_SUPPRESS_PATTERNS = [
  'useAuth must be used within AuthProvider',
];

/**
 * Patterns that should NEVER raise an incident, in any environment.
 * Add benign / unactionable noise here so push alerts stay meaningful.
 */
const ALWAYS_SUPPRESS_PATTERNS = [
  'Cannot close a closed AudioContext',
  'Cannot close an AudioContext twice',
  'AudioContext was not allowed to start',
];

export async function logEvent(
  type: LogType,
  message: string,
  metadata: Record<string, unknown> = {},
  severity?: LogSeverity,
) {
  if (ALWAYS_SUPPRESS_PATTERNS.some((p) => (message || '').includes(p))) {
    return;
  }
  if (import.meta.env.DEV && DEV_SUPPRESS_PATTERNS.some((p) => (message || '').includes(p))) {
    return;
  }
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const sev = severity ?? defaultSeverity(type);
    const fingerprint = await fingerprintFor(type, message);
    await (supabase as any).from("app_logs").insert({
      type,
      message: (message || "").slice(0, 2000),
      metadata,
      user_id: user?.id ?? null,
      severity: sev,
      fingerprint,
    });
  } catch {
    // Never let logging break the app
  }
}

export async function trackPerformance<T>(label: string, fn: () => Promise<T>): Promise<T> {
  const start = performance.now();
  const result = await fn();
  const elapsed = performance.now() - start;
  if (elapsed > SLOW_THRESHOLD_MS) {
    logEvent("SLOW_OPERATION", `${label} took ${Math.round(elapsed)}ms`, {
      label,
      elapsed_ms: Math.round(elapsed),
    }, "warning");
  }
  return result;
}
